import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { AuthService } from '../../services/auth.service';
import { MyAsset } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
import { formatDate } from '../../utils/dateUtils';

/**
 * F10 "My assets": read-only list of the assets the signed-in member holds as custodian (code, name, location,
 * since). No values. Renders nothing when the caller holds no assets; a failed load is reported with a toast.
 */
const MyAssetsCard: React.FC = () => {
  const toast = useToast();
  const [rows, setRows] = useState<MyAsset[] | null>(null);

  useEffect(() => {
    let live = true;
    if (!AuthService.getSelectedInstitution()) { setRows([]); return undefined; }
    FixedAssetTransferService.myAssets()
      .then(r => { if (live) setRows(Array.isArray(r) ? r : []); })
      .catch(e => { if (live) { setRows([]); toast.showApiError(e, 'Failed to load your assets'); } });
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows || rows.length === 0) return null;

  return (
    <Card sx={{ mt: 3 }} data-testid="my-assets-card">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>My assets</Typography>
        <TableContainer>
          <Table size="small" data-help-section="fixedAssets.mine.table">
            <TableHead>
              <TableRow>
                <TableCell data-help="fixedAssets.mine.col.code">Code</TableCell>
                <TableCell data-help="fixedAssets.mine.col.name">Name</TableCell>
                <TableCell data-help="fixedAssets.mine.col.location">Location</TableCell>
                <TableCell data-help="fixedAssets.mine.col.since">Since</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(a => (
                <TableRow key={a.id}>
                  <TableCell>{a.assetCode}</TableCell>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>{a.locationName ?? '—'}</TableCell>
                  <TableCell>{a.since ? formatDate(a.since) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default MyAssetsCard;
